import { useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { verifyPropertyMapping, generateCreatePropertiesSQL, VerificationResult } from '../../utils/verifyPropertyMapping';
import toast from 'react-hot-toast';

export const PropertyMappingVerifier = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerificationResult | null>(null);

  const handleVerify = async () => {
    setLoading(true);
    try {
      const verificationResult = await verifyPropertyMapping();
      setResult(verificationResult);

      if (verificationResult.missing === 0) {
        toast.success('✅ Todas las propiedades están correctamente mapeadas');
      } else {
        toast.error(
          `⚠️ Faltan ${verificationResult.missing} propiedades en la base de datos`,
          { duration: 5000 }
        );
      }
    } catch (error: any) {
      console.error('Error verificando mapeo:', error);
      toast.error(error.message || 'Error al verificar el mapeo');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCopySQL = () => {
    if (!result || result.missingProperties.length === 0) return;

    const sql = generateCreatePropertiesSQL(result.missingProperties);
    navigator.clipboard.writeText(sql);
    toast.success('SQL copiado al portapapeles');
  };

  const handleDownloadSQL = () => {
    if (!result || result.missingProperties.length === 0) return;

    const sql = generateCreatePropertiesSQL(result.missingProperties);
    const blob = new Blob([sql], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `create-missing-properties-${new Date().toISOString().split('T')[0]}.sql`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('SQL descargado');
  };

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-2">Verificar Mapeo de Propiedades</h2>
        <p className="text-gray-600 mb-6">
          Verifica que todas las propiedades del Excel están correctamente mapeadas
          y existen en la base de datos antes de importar.
        </p>

        <Button
          onClick={handleVerify}
          loading={loading}
          variant="primary"
          className="mb-6"
        >
          Verificar Mapeo
        </Button>

        {result && (
          <div className="space-y-6">
            {/* Resumen */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-900">{result.total}</div>
                <div className="text-sm text-blue-700">Total en Mapeo</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-900">{result.found}</div>
                <div className="text-sm text-green-700">Encontradas ✅</div>
              </div>
              <div className={`p-4 rounded-lg ${result.missing > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className={`text-2xl font-bold ${result.missing > 0 ? 'text-red-900' : 'text-green-900'}`}>
                  {result.missing}
                </div>
                <div className={`text-sm ${result.missing > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  Faltantes {result.missing > 0 ? '❌' : '✅'}
                </div>
              </div>
              <div className={`p-4 rounded-lg ${result.extraProperties.length > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <div className={`text-2xl font-bold ${result.extraProperties.length > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                  {result.extraProperties.length}
                </div>
                <div className={`text-sm ${result.extraProperties.length > 0 ? 'text-yellow-700' : 'text-green-700'}`}>
                  Extra en BD {result.extraProperties.length > 0 ? '⚠️' : '✅'}
                </div>
              </div>
            </div>

            {/* Propiedades Encontradas */}
            {result.found > 0 && (
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">
                  ✅ Propiedades Encontradas ({result.found})
                </h3>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-green-900">Propiedad</th>
                        <th className="text-left p-2 text-green-900">Propietario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.foundProperties.map((prop, idx) => (
                        <tr key={idx} className="border-t border-green-200">
                          <td className="p-2 text-green-800">{prop.propertyName}</td>
                          <td className="p-2 text-green-700">{prop.ownerName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Propiedades Faltantes */}
            {result.missing > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">
                  ❌ Propiedades Faltantes ({result.missing})
                </h3>
                <p className="text-sm text-red-700 mb-3">
                  Estas propiedades están en el mapeo pero NO existen en Supabase.
                  Debes crearlas antes de importar datos.
                </p>
                <div className="max-h-60 overflow-y-auto mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 text-red-900">Propiedad</th>
                        <th className="text-left p-2 text-red-900">Propietario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.missingProperties.map((prop, idx) => (
                        <tr key={idx} className="border-t border-red-200">
                          <td className="p-2 text-red-800 font-medium">{prop.propertyName}</td>
                          <td className="p-2 text-red-700">{prop.ownerName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex space-x-3">
                  <Button onClick={handleCopySQL} variant="secondary" size="sm">
                    📋 Copiar SQL
                  </Button>
                  <Button onClick={handleDownloadSQL} variant="secondary" size="sm">
                    💾 Descargar SQL
                  </Button>
                </div>
                <p className="text-xs text-red-600 mt-3">
                  💡 Usa el SQL generado para crear las propiedades faltantes en Supabase
                </p>
              </div>
            )}

            {/* Propiedades Extra */}
            {result.extraProperties.length > 0 && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                  ⚠️ Propiedades en BD sin Mapeo ({result.extraProperties.length})
                </h3>
                <p className="text-sm text-yellow-700 mb-3">
                  Estas propiedades existen en Supabase pero NO están en el mapeo del Excel.
                  No se importarán datos para ellas.
                </p>
                <div className="max-h-40 overflow-y-auto">
                  <ul className="space-y-1">
                    {result.extraProperties.map((prop, idx) => (
                      <li key={idx} className="text-sm text-yellow-800">
                        • {prop.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Estado Final */}
            {result.missing === 0 && (
              <div className="bg-green-100 border-2 border-green-500 p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">🎉</div>
                <div className="text-lg font-semibold text-green-900">
                  ¡Perfecto! El mapeo está completo
                </div>
                <div className="text-sm text-green-700 mt-1">
                  Todas las propiedades del Excel existen en Supabase.
                  Puedes proceder con la importación.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Información */}
        {!result && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              ℹ️ ¿Qué hace esta verificación?
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Comprueba que las 37 propiedades del Excel existen en Supabase</li>
              <li>• Verifica que los nombres coinciden exactamente</li>
              <li>• Detecta propiedades en BD que no están en el Excel</li>
              <li>• Genera SQL para crear propiedades faltantes</li>
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
};
